// src/lib/voiceNoteStorage.js
//
// Client for the voice-note-storage edge function (ticket 6b, section 9.3).
// Rebuilt for the signed-URL upload architecture: audio no longer travels
// through this function's own request body at all. get_upload_url mints a
// short-lived signed PUT straight to the reanlo-voice-reflections bucket,
// and this module PUTs the recorded blob there directly -- the earlier
// draft that base64-encoded the whole recording and posted it through this
// function was the wrong shape for this spec (slow, doubles the transfer,
// risks the function's own body limits for no benefit).
//
// Nothing here decides WHETHER to record, transcribe, or save an entry --
// that is the journal UI's job, and in particular the consent gate
// (src/lib/voiceConsent.js) must already have granted before
// uploadVoiceNote is ever called. The edge function checks the same fact
// again itself, server-side (its own hasConsent) -- this module
// trusting the client gate alone would not be enough, and does not need
// to be, since the server does not trust it either.
//
// Auth: every action reads the caller's own reflections/voice_consents
// rows through RLS ("reader manages own reflections", "reader reads own
// voice consent"), so there is nothing for this module to check or pass
// beyond the user's own session token, which supabase-js already attaches
// to every functions.invoke call automatically.

import * as Sentry from '@sentry/react';
import { supabase } from './supabase';

const FUNCTION_NAME = 'voice-note-storage';

// Every upload failure goes to Sentry, tagged with the step that broke.
// Until this, all three failures below reached only the browser console,
// which on a phone nobody can see: every journal voice note failed for
// weeks (the bucket had no CORS rule) and nothing anywhere said so.
// The error thrown to the caller carries the same step as err.step.
//
// The object path is reflections/<reader id>/<uuid>.wav
// (voice-note-storage's newObjectPath), so it names the reader. The browser
// console, which only the reader sees, keeps it whole. What goes to Sentry
// has the reader id swapped for <reader>, including inside a storage error
// body, which repeats the path it was asked to write.
function withoutReaderId(text, objectPath) {
  const readerId = typeof objectPath === 'string' ? objectPath.split('/')[1] : null;
  return readerId ? text.split(readerId).join('<reader>') : text;
}

function uploadFailure(step, message, consoleMessage, objectPath = null) {
  const err = new Error(message);
  err.step = step;
  const detail = consoleMessage || message;
  console.error(`[voice upload] ${step} —`, detail);
  Sentry.captureException(new Error(`voice note upload (${step}): ${withoutReaderId(detail, objectPath)}`), {
    tags: { voice_step: step },
  });
  return err;
}

// Every action goes through this one helper. Two error sources, same as
// useVoiceRecorder.js's own supabase.functions.invoke('voice-to-text', ...)
// call: a transport-level invokeError, or a data.error body on success.
//
// One deliberate addition over that existing pattern: supabase-js (2.x)
// treats ANY non-2xx edge function response as a FunctionsHttpError, with
// `data` left null -- so every respond({ error, status }) this project's
// edge functions return (401/400/403/404/413/500) actually arrives here
// as invokeError, not data.error. voice-to-text's own call site never
// needed to look inside that error (it only ever logs the message and
// shows one generic "transcription failed" string to the reader), so its
// simpler `if (data?.error) throw new Error(data.error)` line is
// effectively unreachable for its own function today, harmlessly.
// getVoiceNoteUrl below can't afford that: it has to tell "no note
// stored" (expected, return null) apart from a real failure (should
// throw), and both are 404s the function distinguishes only by message
// text. So this helper reaches into invokeError.context -- the raw
// Response object FunctionsHttpError carries -- to recover the actual
// { error: "..." } body Deno.serve sent, rather than only ever seeing the
// generic "Edge Function returned a non-2xx status code".
async function callVoiceNoteStorage(action, params) {
  const { data, error: invokeError } = await supabase.functions.invoke(FUNCTION_NAME, {
    body: { action, ...params },
  });
  if (invokeError) {
    let message = invokeError.message;
    try {
      const body = await invokeError.context?.json?.();
      if (body?.error) message = body.error;
    } catch {
      // context wasn't JSON or was already consumed -- fall back to
      // invokeError.message rather than masking it with a second error.
    }
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

// Step one of the upload flow (9.3, steps 2-9). Asks the edge function for
// a signed PUT URL scoped to exactly this upload -- this reader's own
// prefix, audio/wav content-type, checked against the 3-minute cap and
// against server-side consent -- then PUTs the blob straight to GCS,
// never through this function's own body.
//
// blob: a WAV Blob -- the same encodeWav() output useVoiceRecorder.js
// already produces for transcription, reused here rather than inventing a
// second audio encoding for the one browser-recorded format this app
// ever produces.
//
// durationSeconds: the actual recorded length, capped client-side at 3:00
// by useVoiceRecorder.js -- sent so the edge function can sanity-check it
// against MAX_DURATION_SECONDS before minting anything. Never trusted as
// the final source of truth for what gets saved on the row; the caller
// reads its own recorder state for that.
//
// Returns the object_path the caller must save on the reflections row as
// audio_url (NOT a URL -- see the migration's own column comment) once
// the insert actually happens (9.3 step 9). This function does not touch
// the reflections table at all -- it only gets bytes into the bucket.
// Order matters at the call site: upload here first, then insert the row;
// if the insert then fails, call deleteOrphanedUpload with this same
// object_path before showing the reader an error (9.3: "no orphans").
//
// Failure diagnosis: this has two genuinely different failure points, and
// they get logged separately (console.error, not just thrown) so the
// browser console says WHICH step broke rather than one flat "upload
// failed" that could mean either:
//   1. get_upload_url -- a call to OUR OWN edge function. A failure here
//      is a Supabase/auth/consent/deploy problem: not authenticated, the
//      function isn't deployed with this action yet, consent not granted
//      server-side, or a missing GOOGLE_CLOUD_STORAGE_JSON_VOICE_REFLECTIONS
//      / VOICE_BUCKET_NAME secret.
//   2. the PUT itself -- a request straight to storage.googleapis.com,
//      never touching our edge function. fetch() throwing here (rather
//      than resolving with a bad status) almost always means the GCS
//      bucket's CORS config doesn't allow this origin/method: the browser
//      blocks the request before any status comes back, and JS can never
//      read WHY -- only the browser's own console (not this code) prints
//      the actual "blocked by CORS policy" line. A PUT that DOES resolve
//      but with !ok is a real response from GCS (bad/expired signature,
//      permission, etc.) and its body is logged here since GCS's XML
//      error body usually says exactly what's wrong.
export async function uploadVoiceNote(blob, durationSeconds) {
  let url, object_path;
  try {
    ({ url, object_path } = await callVoiceNoteStorage('get_upload_url', {
      duration_seconds: Math.round(durationSeconds || 0),
    }));
  } catch (err) {
    throw uploadFailure('get_upload_url', err.message);
  }

  let putResponse;
  try {
    putResponse = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'audio/wav' },
      body: blob,
    });
  } catch (err) {
    // A connection drop also lands here, but a CORS block always does, and
    // the browser never says which: only its own console prints "blocked by
    // CORS policy". The bucket's CORS rule is a Google Cloud setting, not
    // code in this repo; scripts/check-voice-fallback.mjs's header has the
    // probe that shows whether it is set.
    throw uploadFailure(
      'put_unreachable',
      "Couldn't reach storage to upload the recording.",
      `PUT to storage never completed for ${object_path} (a CORS block on the bucket, or the connection dropped): ${err.message}`,
      object_path,
    );
  }

  if (!putResponse.ok) {
    const body = await putResponse.text().catch(() => '(no response body)');
    throw uploadFailure(
      'put_rejected',
      `Uploading the recording failed (${putResponse.status} ${putResponse.statusText}).`,
      `storage rejected the PUT for ${object_path}: ${putResponse.status} ${putResponse.statusText}, body: ${body.slice(0, 500)}`,
      object_path,
    );
  }

  return object_path;
}

// Cleanup for the "upload succeeded, then the reflections insert failed"
// case (9.3: "no orphans"). Call with the exact object_path
// uploadVoiceNote just returned -- there is no reflection row to key an
// ordinary delete off yet, which is exactly why this is a separate action
// (delete_orphan) rather than reusing deleteVoiceNote below.
//
// Best-effort on purpose: logs rather than throws. The caller is already
// in its own error-handling path for the insert failure, and a leftover
// orphaned object is a storage-cost annoyance, not a reason to replace
// the error the reader actually needs to see with a second one about
// cleanup.
export async function deleteOrphanedUpload(objectPath) {
  try {
    await callVoiceNoteStorage('delete_orphan', { object_path: objectPath });
  } catch (err) {
    console.error('deleteOrphanedUpload failed for', objectPath, err.message);
  }
}

// Signed, short-lived (15 minutes -- server-enforced, this module has no
// say over the TTL), minted fresh on every call -- never cached here.
// Returns null if the reflection has no stored note rather than throwing,
// so a caller can use it as a simple "is there anything to play" check
// without a try/catch for the common case.
export async function getVoiceNoteUrl(reflectionId) {
  try {
    const data = await callVoiceNoteStorage('get_playback_url', { reflection_id: reflectionId });
    return data?.url || null;
  } catch (err) {
    if (String(err.message || '').includes('No voice note stored')) return null;
    throw err;
  }
}

// Hard delete for one entry (9.10, event 1): removes the GCS object and
// clears audio_url/transcript/duration_seconds on the reflections row,
// object first, then the row's pointer to it -- enforced by the edge
// function's own handleDelete ordering, not repeated here. Safe to call
// on a reflection that has no stored note -- deleteObject is idempotent
// server-side, same contract as before.
export async function deleteVoiceNote(reflectionId) {
  await callVoiceNoteStorage('delete', { reflection_id: reflectionId });
}

// Account deletion (9.10, event 2). Deletes every object under this
// reader's own reflections/<reader_id>/ prefix, regardless of which
// reflection rows still reference them -- there is no reflection_id
// parameter because there does not need to be one; the prefix comes from
// the caller's own verified session, server-side.
//
// Call this BEFORE deleting the reader's own profile row and signing out
// (see SettingsPage.jsx's handleDeleteAccount) -- the caller must still
// be authenticated for this call to reach the edge function at all.
//
// Does not throw on a partial failure -- it surfaces one instead, the
// same "declined and succeeded must not look the same" standard this
// codebase already applies elsewhere. The caller decides whether a
// non-empty `failed` list should still let account deletion proceed or
// should stop and ask the reader to retry; this module has no opinion.
export async function deleteAllVoiceNotesForAccount() {
  const data = await callVoiceNoteStorage('delete_all_for_reader', {});
  return { deleted: data?.deleted ?? 0, failed: data?.failed ?? [] };
}
